import { useQuery } from '@tanstack/react-query'
import type { Balance, LeaveTypeId } from '@lms/shared-types'
import { isLeaveTypeExhausted, LEAVE_BALANCE_ORDER } from '../../lib/leave-type-meta'
import { AccrualBar } from './AccrualBar'
import { ProRatedBar } from './ProRatedBar'
import { HealthBarCard } from './HealthBarCard'
import { PersonalBlockSkeleton } from './PersonalBlockSkeleton'
import { useHealthBar, useWellbeingNarrative } from '../../api/health-bar'
import { flags } from '../../lib/feature-flags'
import { getMe, listMyBalances } from '../../api/me'
import { getEmployee, listEmployeeBalances } from '../../api/employees'
import { useSession } from '../../auth/auth-context'
import { useEffectiveEmployeeId } from '../shell/effective-employee'
import { LEAVE_TYPE_STYLE } from '../../lib/leave-type-palette'
import { NEGATIVE_BALANCE_COLOR, shouldShowNegativeBalance } from '../../lib/negative-balance'
import { useReplacementCap } from '../../api/me-replacement-cap'
import { useUnpaidUsage } from '../../api/me-unpaid-usage'


// ─── Personal block ──────────────────────────────────────────────────────────
// Wellbeing (HealthBarCard) + Leave balances (BalancesCard) — shown at the top
// of every role's home page so managers / HOD / director / HR / exec-director
// see their own status alongside the role-specific work surface.

type Gender = 'M' | 'F' | 'X' | null

// Normalised personal-profile shape used by the home block. Sourced from
// either /me (self) or /employees/{id} (impersonation) — both carry these
// fields under the same names, so we collapse them to one type here.
interface PersonalProfile {
  gender: Gender
  birthDate: string | null
  onProbation: boolean
  joinDate: string | null
  lastWorkingDay: string | null
  contractEndDate: string | null
  annualEntitlement: number | undefined
  // An annual override bypasses accrual (flat grant, no monthly ramp), so the
  // AccrualBar is hidden when this is true.
  hasAnnualOverride: boolean
}

// Order matches prototype. Maternity/Paternity are gender-gated so only the
// relevant one renders per viewer. Unknown gender → omit both (HR can edit
// gender on /profile and the tile will appear).
function otherLeaveTypesFor(gender: Gender): ReadonlyArray<LeaveTypeId> {
  // Derive from the shared canonical order (leave-type-meta) so this and the
  // manager PersonModal stay in lockstep. Drop annual (rendered first) and unpaid
  // (added conditionally by the caller), and keep only the applicable parental type.
  const parentalType: LeaveTypeId | null =
    gender === 'F' ? 'maternity' : gender === 'M' ? 'paternity' : null
  return LEAVE_BALANCE_ORDER.filter((t) => {
    if (t === 'annual' || t === 'unpaid') return false
    if (t === 'maternity' || t === 'paternity') return t === parentalType
    return true
  })
}

/** True once today's date is past the employee's birth-month in the current year.
 *  Birthday leave is a 1-day per-year entitlement that does not carry forward — once
 *  the birth month has elapsed it is forfeit, so the tile renders as '—'. */
function isBirthdayExpiredForYear(today: Date, birthDate: string | null): boolean {
  if (!birthDate) return false
  const parts = birthDate.split('-')
  if (parts.length < 2) return false
  const birthMonth = Number(parts[1])
  if (!Number.isFinite(birthMonth) || birthMonth < 1 || birthMonth > 12) return false
  return today.getMonth() + 1 > birthMonth
}

interface BalancesCardProps {
  balances: ReadonlyArray<Balance>
  year: number
  isLoading: boolean
  gender: Gender
  birthDate: string | null
  onProbation: boolean
  /** Effective employee id when HR is "Viewing as" someone, else null. Routes
   *  the usage/cap tiles (unpaid, replacement) to their employee-scoped
   *  endpoints, mirroring the balance tiles beside them. */
  viewAsEmployeeId: string | null
}

function BalancesCard({
  balances,
  year,
  isLoading,
  gender,
  birthDate,
  onProbation,
  viewAsEmployeeId,
}: BalancesCardProps): JSX.Element {
  // Unpaid is appended last, behind its flag — it's a usage tile ("days applied"),
  // not an entitlement, so it sits after the quota tiles. Only shown once the
  // employee has actually applied for unpaid leave this year (days > 0);
  // otherwise there's nothing to report and the tile stays hidden. Under
  // "Viewing as", reads the impersonated employee's usage (see hook doc).
  const unpaidUsage = useUnpaidUsage(viewAsEmployeeId)
  const hasUnpaidUsage = (unpaidUsage.data?.daysApplied ?? 0) > 0
  const leaveTypes: ReadonlyArray<LeaveTypeId> = [
    'annual',
    ...otherLeaveTypesFor(gender),
    ...(flags.unpaidUsageTile && hasUnpaidUsage ? (['unpaid'] as const) : []),
  ]
  // Replacement cap is likewise per-employee; under "Viewing as" it must read
  // the impersonated employee's grants, not the HR viewer's (see hook doc).
  const replacementCap = useReplacementCap(viewAsEmployeeId)
  const birthdayExpired = isBirthdayExpiredForYear(new Date(), birthDate)
  const birthdayUnavailableReason: 'probation' | 'expired' | null = birthdayExpired
    ? 'expired'
    : onProbation
      ? 'probation'
      : null

  return (
    <div
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 14,
        padding: '14px 18px',
      }}
      data-testid="balances-card"
    >
      <div style={{
        fontSize: 10,
        color: 'var(--text-2)',
        textTransform: 'uppercase',
        letterSpacing: '.08em',
        fontWeight: 700,
      }}>
        My leave · {year}
      </div>

      <div
        className="balance-tiles"
        style={{ marginTop: 14, display: 'grid', gap: 10 }}
      >
        {leaveTypes.map((type) => {
          const b = balances.find((x) => x.leaveType === type)
          // Unified eligibility (flag on): any type the server marks not-yet-
          // eligible (tenure/probation, independent of balance) renders a "NOT
          // YET ELIGIBLE" badge. Birthday forfeiture (month elapsed) is a
          // distinct state and keeps its own "EXPIRED" badge. When the flag is
          // off, fall back to the legacy birthday-only probation/expiry surfacing.
          const forceUnavailable: 'probation' | 'expired' | 'ineligible' | null = (() => {
            // Not-yet-eligible is the more fundamental state, so it wins over
            // birthday forfeiture (a <12-month employee reads "NOT YET ELIGIBLE",
            // not "EXPIRED"). An eligible-but-forfeited birthday still reads EXPIRED.
            if (flags.leaveEligibilityBadge && b && b.eligible === false) return 'ineligible'
            if (type === 'birthday' && birthdayExpired) return 'expired'
            if (flags.leaveEligibilityBadge) return null
            return type === 'birthday' ? birthdayUnavailableReason : null
          })()
          return (
            <BalanceTile
              key={type}
              type={type}
              balance={b ?? null}
              replacementDays={type === 'replacement' ? (replacementCap.data?.remainingDays ?? null) : null}
              unpaidDaysApplied={
                type === 'unpaid' ? (unpaidUsage.data?.daysApplied ?? null) : undefined
              }
              isLoading={isLoading}
              forceUnavailable={forceUnavailable}
            />
          )
        })}
      </div>
    </div>
  )
}

interface BalanceTileProps {
  type: LeaveTypeId
  balance: Balance | null
  replacementDays: number | null
  /** For the unpaid tile only: unpaid days *applied for* this year (includes pending). Unpaid leave has
   *  no balance, so it renders as a usage count instead of "of N left". `null`
   *  while the usage query is loading; `undefined` for every other tile (which
   *  keeps this out of the usage branch entirely). */
  unpaidDaysApplied?: number | null | undefined
  isLoading: boolean
  /** When set, the tile is rendered as disabled regardless of the balance row.
   *  'probation' / 'expired' are legacy birthday-specific states; 'ineligible'
   *  is the unified "NOT YET ELIGIBLE" state driven by the balance `eligible`
   *  field (tenure/probation gates, independent of the balance number). */
  forceUnavailable?: 'probation' | 'expired' | 'ineligible' | null
  /** Default true. When false, the tile is a plain non-navigating display
   *  block (no /apply link, no pointer cursor) — used where the viewer isn't
   *  the balance owner, e.g. a manager reading a report's balances. */
  interactive?: boolean
}

export function BalanceTile({
  type,
  balance,
  replacementDays,
  unpaidDaysApplied,
  isLoading,
  forceUnavailable = null,
  interactive = true,
}: BalanceTileProps): JSX.Element {
  const style = LEAVE_TYPE_STYLE[type]

  // Unpaid usage tile: no entitlement/balance, so it shows days *applied for* this
  // year rather than an "of N left" quota. Only engaged when the caller passes
  // `unpaidDaysApplied` (the home grid behind the flag) — other BalanceTile
  // callers rendering unpaid fall through to the normal (disabled) path.
  const isUnpaidUsage = type === 'unpaid' && unpaidDaysApplied !== undefined

  const LABEL_OVERRIDES: Partial<Record<LeaveTypeId, string>> = {
    medical: 'Sick',
  }
  const rawLabel = LABEL_OVERRIDES[type] ?? style.label
  const label = rawLabel.toUpperCase()

  // The tile's denominator is the *currently available cap* — the amount
  // the employee could book today. For ANNUAL during the first 12 months
  // this is the accrued slice (rises monthly), not the full yearly grant;
  // showing the full grant misleads new joiners into thinking they have
  // days they haven't accrued yet. For all other leave types the server
  // returns `accruedEntitlement === entitlement` so this is a no-op.
  const cap = balance ? (balance.accruedEntitlement ?? balance.entitlement) : 0
  const total = balance ? cap + balance.carryOver : 0
  // Accrual-stage over-draw: a first-year employee who booked more annual leave
  // than they've accrued so far has a genuine negative balance. Surface it (in
  // the danger hue) instead of flooring at 0 so the deficit isn't hidden.
  const showNegative =
    !!balance && shouldShowNegativeBalance(balance, flags.negativeAccrualBalance)
  const remaining = balance
    ? showNegative
      ? balance.available
      : Math.max(0, balance.available)
    : 0

  // Replacement leave does NOT use balance.entitlement — grants are a separate
  // first-class entity surfaced via useReplacementCap. The tile is bookable
  // whenever the employee has unconsumed grant days.
  // Exhausted non-Annual types are hard-capped: once nothing remains they are
  // not bookable from the tile. Annual is exempt — it overflows to Unpaid.
  //
  // "No allocation" is a plain zero, not "exhaustion": the employee has nothing
  // available right now AND never used or committed anything, so it renders as
  // "0" with no "USED" badge. This covers a MISSING balance row, a present row
  // with zero entitlement (e.g. marriage for a probationer — confirmed-employees-
  // only), and replacement leave for someone who never earned a grant. Badging
  // any of those "USED" is wrong: nothing was used. A row/grant with days
  // actually used or pending (grant spent, entitlement reduced after taking
  // leave) is real exhaustion and still shows "USED". Replacement's current
  // allocation is its grant cap, not balance.entitlement; every other type uses
  // the entitlement total.
  const usedOrPending = balance ? balance.used + balance.pending : 0
  const hasCurrentAllocation =
    type === 'replacement' ? replacementDays !== null && replacementDays > 0 : total > 0
  const noAllocation = !isUnpaidUsage && !hasCurrentAllocation && usedOrPending === 0
  // "Exhausted" = had something and used it up (→ the "USED" badge). For
  // replacement the grant cap and the derived balance row can diverge (an
  // imported replacement leave with no matching grant record leaves the row's
  // `available` positive while the cap is 0), so usage — not the row's
  // `available` — is the honest signal. Every other type uses the shared
  // remaining<=0 rule.
  const exhausted =
    !noAllocation &&
    (type === 'replacement' ? usedOrPending > 0 : isLeaveTypeExhausted(type, remaining))
  // The unpaid usage tile is never "disabled" (it's informational, not a
  // bookable quota) — it always renders its days-applied count.
  const disabled =
    !isUnpaidUsage &&
    (forceUnavailable !== null ||
      (type === 'replacement'
        ? replacementDays === null || replacementDays <= 0
        : !balance || total === 0 || exhausted))

  const counter = (() => {
    if (isLoading) return '…'
    if (isUnpaidUsage) return unpaidDaysApplied === null ? '…' : String(unpaidDaysApplied)
    if (type === 'replacement') {
      return replacementDays === null ? '—' : String(replacementDays)
    }
    // No balance row → a plain "0" (treated as zero), not a disabled dash.
    if (noAllocation) return '0'
    if (disabled) return '—'
    return String(remaining)
  })()

  const caption = (() => {
    if (isLoading) return null
    if (isUnpaidUsage) return 'days applied this year'
    if (disabled || type === 'replacement') return null
    if (showNegative) return 'overdrawn'
    return `of ${total} left`
  })()

  // Colour the headline number + progress bar with the leave type's own hue
  // (from the shared palette), muted grey when the tile is inactive. The bar
  // shows the remaining fraction; hidden for replacement (grant-based, no
  // denominator), the unpaid usage tile (no quota), and any disabled tile.
  const accent = LEAVE_TYPE_STYLE[type].swatch
  const numberColor = disabled
    ? 'var(--text-3)'
    : showNegative
      ? NEGATIVE_BALANCE_COLOR
      : accent
  // The bar shows the remaining fraction; a negative (overdrawn) balance reads
  // as an empty bar, not a reversed one.
  const pct =
    !disabled && total > 0 ? Math.min((Math.max(0, remaining) / total) * 100, 100) : 0
  const showBar =
    !isLoading && !disabled && !isUnpaidUsage && type !== 'replacement' && total > 0

  return (
    <a
      href={disabled || !interactive || isUnpaidUsage ? undefined : `/apply?type=${type}`}
      aria-disabled={disabled ? true : undefined}
      tabIndex={disabled || !interactive || isUnpaidUsage ? -1 : undefined}
      style={{
        textAlign: 'left',
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 10,
        padding: '12px 14px',
        opacity: disabled ? 0.6 : 1,
        minHeight: 82,
        textDecoration: 'none',
        color: 'inherit',
        display: 'block',
        pointerEvents: disabled ? 'none' : undefined,
        cursor: disabled || !interactive || isUnpaidUsage ? 'default' : 'pointer',
      }}
    >
      <div style={{
        fontSize: 9.5,
        fontWeight: 700,
        color: 'var(--text-2)',
        textTransform: 'uppercase',
        letterSpacing: '.06em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        marginBottom: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}>
        {label}
        {/* "NOT YET ELIGIBLE" surfaces even when there is no allocation —
             eligibility is independent of the balance number. The other badges
             (EXPIRED/PROBATION/USED) only make sense against a real allocation. */}
        {disabled &&
        (forceUnavailable === 'ineligible' ||
          (!noAllocation && (forceUnavailable !== null || exhausted))) ? (
          <span style={{
            background: 'var(--surface-2)',
            border: '1px solid var(--border-strong)',
            color: 'var(--text-3)',
            padding: '1px 5px',
            borderRadius: 4,
            fontSize: 8.5,
            letterSpacing: '.04em',
            fontWeight: 700,
          }}>
            {forceUnavailable === 'expired'
              ? 'EXPIRED'
              : forceUnavailable === 'ineligible'
                ? 'NOT YET ELIGIBLE'
                : forceUnavailable === 'probation'
                  ? 'PROBATION'
                  : 'USED'}
          </span>
        ) : null}
      </div>

      <div style={{
        fontSize: 22,
        fontWeight: 800,
        color: numberColor,
        letterSpacing: '-.01em',
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.05,
      }}>
        {counter}
      </div>

      {showBar ? (
        <div style={{
          height: 3,
          background: 'var(--surface-2)',
          borderRadius: 99,
          marginTop: 8,
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${pct}%`,
            background: accent,
            borderRadius: 99,
          }} />
        </div>
      ) : null}

      {caption ? (
        <div style={{
          fontSize: 10,
          fontWeight: 500,
          color: 'var(--text-3)',
          marginTop: 6,
          fontVariantNumeric: 'tabular-nums',
        }}>
          {caption}
        </div>
      ) : null}
    </a>
  )
}

/**
 * Personal block — viewer's own wellbeing card + leave balances tile grid.
 *
 * Shown at the top of every role's home page (employee, manager, HOD,
 * director, HR, exec-director). Self-contained data fetching: keys match
 * the queries used elsewhere so React Query dedupes the fetches.
 *
 * Impersonation: when an HR user is impersonating an employee, the
 * EmployeeHomeDashboard route is what loads — so `useEffectiveEmployeeId`
 * resolves to the impersonated user. On the role dashboards (manager / HR
 * / director / etc), the viewer is always the real user.
 */
export function PersonalBlock(): JSX.Element {
  const { user } = useSession()
  const realId = user?.id ?? null
  const effectiveId = useEffectiveEmployeeId(realId)
  const isImpersonating = effectiveId !== null && effectiveId !== realId
  const year = new Date().getFullYear()

  // Profile source is impersonation-aware: when HR is "Viewing as" an
  // employee, read that employee's row (join date, entitlement, gender, …)
  // so the accrual bar and balances tile reflect the impersonated user —
  // not the HR viewer's own /me. `getMe` and `getEmployee` share these
  // field names, so we normalise both into one shape here (typing the
  // queryFn keeps TanStack from collapsing the Me | Employee union).
  const profileQuery = useQuery<PersonalProfile>({
    queryKey: isImpersonating && effectiveId ? ['employee-profile', effectiveId] : ['me'],
    queryFn: async () => {
      const row =
        isImpersonating && effectiveId ? await getEmployee(effectiveId) : await getMe()
      return {
        gender: row.gender ?? null,
        birthDate: row.birthDate ?? null,
        onProbation: row.onProbation ?? false,
        joinDate: row.joinDate ?? null,
        lastWorkingDay: row.lastWorkingDay ?? null,
        contractEndDate: row.contractEndDate ?? null,
        annualEntitlement: row.annualEntitlement,
        hasAnnualOverride: row.hasAnnualOverride ?? false,
      }
    },
    enabled: Boolean(effectiveId),
  })
  const profile = profileQuery.data
  const gender: Gender = profile?.gender ?? null
  const birthDate: string | null = profile?.birthDate ?? null
  const onProbation: boolean = profile?.onProbation ?? false
  // Accrual bar inputs — joinDate is always available on a real profile row,
  // lastWorkingDay / contractEndDate are nullable. The AccrualBar self-hides
  // for tenured employees with no exit date, so we can pass these
  // unconditionally and let the component decide whether to render.
  const joinDate: string | null = profile?.joinDate ?? null
  const lastWorkingDay: string | null = profile?.lastWorkingDay ?? null
  const contractEndDate: string | null = profile?.contractEndDate ?? null

  const balancesQuery = useQuery({
    queryKey: ['me', 'balances', effectiveId, year],
    queryFn: () =>
      isImpersonating && effectiveId
        ? listEmployeeBalances(effectiveId, year)
        : listMyBalances(year),
    enabled: Boolean(effectiveId),
  })

  // When impersonating, read the impersonated employee's health bar (HR+ only,
  // enforced server-side) so the "Viewing as" card shows their band/score —
  // not the HR viewer's own /me projection.
  const healthBar = useHealthBar(isImpersonating ? effectiveId : null)
  const showHealthBar = healthBar.data !== undefined

  // ILMU wellbeing narrative (restored post-SG#1, flag-gated). Follows the same
  // self / "Viewing as" split as the health bar: own card hits /me, an
  // impersonating HR viewer hits /employees/{id}/wellbeing-narrative so the
  // narrative describes the impersonated employee, not the HR viewer.
  const narrativeEnabled = flags.ilmuWellbeingNarrative && showHealthBar
  const wellbeingNarrative = useWellbeingNarrative(
    narrativeEnabled,
    isImpersonating ? effectiveId : null,
  )
  // employee-scoped variant, so during impersonation the card falls back to

  const balances = balancesQuery.data ?? []
  const isLoading = balancesQuery.isLoading
  // The AccrualBar needs the FULL annual grant (the HR-set per-employee
  // entitlement before any join/leaver proration). The balance row's
  // `entitlement` field is post-proration (e.g. 18 → 15 for a leaver
  // with LWD Oct 31), and feeding that into the bar would double-claw
  // the visualization. Source the full grant from /me instead — the
  // bar then computes its own join/leaver math and arrives at the same
  // cap the BalancesCard tile already shows.
  const annualEntitlement = profile?.annualEntitlement
  // An annual override is a flat grant — it bypasses BOTH first-year accrual and
  // leaver proration (see compute_available). So neither the AccrualBar nor the
  // ProRatedBar applies; hide both when the employee has an override.
  const hasAnnualOverride = profile?.hasAnnualOverride ?? false

  // Show a skeleton that mirrors the real layout while the core data (profile,
  // balances, health bar) is still in flight — rather than flashing `…`
  // counters and popping the Leave Rhythm card in late. Gated on effectiveId so
  // a disabled (not-yet-resolved) query can't leave us stuck on the skeleton;
  // cached responses resolve isPending → false immediately, so no flash.
  const coreLoading =
    Boolean(effectiveId) &&
    (profileQuery.isPending || balancesQuery.isPending || healthBar.isPending)
  if (coreLoading) {
    return <PersonalBlockSkeleton />
  }

  return (
    <>
      {flags.homeAccrualBars && joinDate && annualEntitlement !== undefined && !hasAnnualOverride ? (
        // ProRatedBar takes priority when the employee has an exit date
        // in the current year — that's the more urgent signal. Otherwise
        // AccrualBar shows the first-year monthly drip. Both components
        // self-hide when they don't apply (e.g. tenured employees with
        // no exit see neither).
        (lastWorkingDay || contractEndDate) ? (
          <ProRatedBar
            entitlement={annualEntitlement}
            lastWorkingDay={lastWorkingDay}
            contractEndDate={contractEndDate}
          />
        ) : (
          <AccrualBar
            entitlement={annualEntitlement}
            joinDate={joinDate}
          />
        )
      ) : null}
      <div
        className="dash-grid"
        style={{
          marginTop: 18,
          display: 'grid',
          gap: 16,
          alignItems: 'stretch',
        }}
      >
        {showHealthBar && healthBar.data ? (
          <HealthBarCard
            health={healthBar.data}
            narrativeLoading={narrativeEnabled && wellbeingNarrative.isLoading}
            {...(narrativeEnabled && wellbeingNarrative.data
              ? { narrative: wellbeingNarrative.data }
              : {})}
          />
        ) : null}
        <BalancesCard
          balances={balances}
          year={year}
          isLoading={isLoading}
          gender={gender}
          birthDate={birthDate}
          onProbation={onProbation}
          viewAsEmployeeId={isImpersonating && effectiveId ? effectiveId : null}
        />
      </div>
    </>
  )
}
